<script setup lang="ts">
/**
 * **代码控件**（多行、等宽）—— JS 节点的 `code` 行用它。
 *
 * ⚠️ 三个和普通文本输入不同的地方（这也是它单独一个控件的原因）：
 *   · **Tab 键插入缩进**，而不是跳走焦点 —— 写代码时 Tab 跳走最恼人；
 *   · **不折行**（`white-space: pre` + 横向滚动）：折行会让缩进错乱，
 *     代码读起来完全变样；
 *   · 高度可拖（右下角），和文本控件同一套交互 —— 节点高度随内容自适应，
 *     所以"把节点改高"就是拖它（Blender 也不支持直接拖节点改高）。
 */
import { computed, ref, watch } from 'vue'
import type { CodeWidget } from '@/types/widget'

const props = defineProps<{ widget: CodeWidget }>()
const emit = defineEmits<{
  (e: 'update', value: string): void
  (e: 'resize', height: number): void
}>()

const el = ref<HTMLTextAreaElement | null>(null)
/** 正在输入时用本地草稿，避免每次按键都被父组件的格式化打断 */
const draft = ref<string | null>(null)
const shown = computed(() => draft.value ?? props.widget.value)

watch(
  () => props.widget.value,
  () => {
    draft.value = null
  },
)

function onInput(event: Event): void {
  draft.value = (event.target as HTMLTextAreaElement).value
}

function onBlur(): void {
  if (draft.value === null) return
  const next = draft.value
  draft.value = null
  if (next !== props.widget.value) emit('update', next)
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Tab') return
  /* Tab = 两个空格（缩进），不是跳焦点 */
  event.preventDefault()
  const target = event.target as HTMLTextAreaElement
  const start = target.selectionStart
  const end = target.selectionEnd
  const next = `${target.value.slice(0, start)}  ${target.value.slice(end)}`
  target.value = next
  target.selectionStart = target.selectionEnd = start + 2
  draft.value = next
}
</script>

<template>
  <div class="np-widget np-widget--code nodrag">
    <textarea
      ref="el"
      class="np-widget__field np-widget__code"
      spellcheck="false"
      autocomplete="off"
      :rows="widget.rows ?? 4"
      :placeholder="widget.placeholder"
      :value="shown"
      @input="onInput"
      @blur="onBlur"
      @keydown="onKeydown"
      @pointerdown.stop
    />
  </div>
</template>
